import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useLocation } from "react-router-dom";

// Types and endpoints
type ChatMessage = { sender: string; text: string; time: number };
const CHAT_ENDPOINT = 'wss://support-chat.hivetivity.workers.dev/chat';
const HISTORY_ENDPOINT = 'https://support-chat.hivetivity.workers.dev/chat/history';

const LOGO_URL = "https://imagedelivery.net/ylmXutM6_iJT3CT9AqBdjQ/2f8dd435-d5c7-4e72-276a-7f66ca306400/public";
const BIZZY_URL = "https://imagedelivery.net/ylmXutM6_iJT3CT9AqBdjQ/5a70a94f-d0da-42d7-6f5b-45cd6f733600/public";

const BRAND = "#4ba3c7";

// Launcher logo (optimized for 80px button)
const Avatar = () => (
  <img
    src={LOGO_URL}
    alt="Hivetivity"
    style={{
      width: 60,
      height: 60,
      borderRadius: "50%",
      background: "#fff",
      border: "3px solid #fff",
      boxShadow: "0 2px 8px 0 rgba(86,160,208,0.12)",
      objectFit: "cover",
      objectPosition: "center"
    }}
  />
);

// Bizzy agent avatar (bigger)
const BizzyAvatar = () => (
  <img
    src={BIZZY_URL}
    alt="Bizzy the Bee"
    style={{
      width: 44,
      height: 44,
      borderRadius: "50%",
      marginRight: 10,
      flexShrink: 0,
      background: "#fff",
      border: "2px solid #f3fafe",
      objectFit: "cover"
    }}
  />
);

const ChatWidgetInner: React.FC = React.memo(() => {
  const { user, ready } = useAuth();
  const [uuid, setUuid] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [open, setOpen] = useState(false);
  const [isAgentTyping, setIsAgentTyping] = useState(false);
  const [isUserTyping, setIsUserTyping] = useState(false);
  const [renderKey, setRenderKey] = useState(0);
  const [lastTypingTime, setLastTypingTime] = useState(0);
  const [showAttentionDots, setShowAttentionDots] = useState(false);
  const [dotsShownCount, setDotsShownCount] = useState(0);
  const wsRef = useRef<WebSocket | null>(null);
  
  // Calculate if agent is currently typing (within last 5 minutes)
  const isTypingActive = lastTypingTime > 0 && (Date.now() - lastTypingTime) < 300000;

  // Play gentle notification sound for incoming agent messages
  const playUserNotificationSound = () => {
    try {
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      
      // Create a gentle, pleasant notification chime
      const playTone = (frequency: number, startTime: number, duration: number, volume: number = 0.3) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.setValueAtTime(frequency, startTime);
        oscillator.type = 'sine'; // Soft, pleasant tone
        
        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(volume, startTime + 0.05);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
        
        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
      };
      
      const now = audioContext.currentTime;
      // Gentle two-tone chime (low to high)
      playTone(600, now, 0.4, 0.25);        // First tone (lower, soft)
      playTone(800, now + 0.1, 0.5, 0.3);   // Second tone (higher, slightly louder)
    } catch (error) {
      console.log('Could not play user notification sound:', error);
    }
  };
  
  // Force re-render every 1.4 seconds to update isTypingActive calculation
  useEffect(() => {
    if (lastTypingTime > 0) {
      const interval = setInterval(() => {
        setRenderKey(prev => prev + 1);
      }, 1400);
      
      // Clear interval after 5 minutes
      setTimeout(() => clearInterval(interval), 300000);
      
      return () => clearInterval(interval);
    }
  }, [lastTypingTime]);

  // Attention animation - only show for non-authenticated users, max 5 times
  useEffect(() => {
    if (!open && ready && !user && dotsShownCount < 5) {
      // Show dots after 4 seconds on page load
      const initialTimeout = setTimeout(() => {
        if (dotsShownCount < 5) {
          setShowAttentionDots(true);
          setDotsShownCount(prev => prev + 1);
          setTimeout(() => setShowAttentionDots(false), 3000);
        }
      }, 4000);

      // Then show every 6 seconds until limit reached
      const showDotsInterval = setInterval(() => {
        if (dotsShownCount < 5) {
          setShowAttentionDots(true);
          setDotsShownCount(prev => prev + 1);
          setTimeout(() => setShowAttentionDots(false), 3000);
        } else {
          // Stop the interval once we've shown 5 times
          clearInterval(showDotsInterval);
        }
      }, 6000);

      return () => {
        clearTimeout(initialTimeout);
        clearInterval(showDotsInterval);
      };
    }
  }, [open, ready, user, dotsShownCount]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const typingTimeout = useRef<NodeJS.Timeout | null>(null);

  // Generate or load UUID
  useEffect(() => {
    let id = localStorage.getItem("chat_uuid");
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem("chat_uuid", id);
    }
    setUuid(id!);
  }, []);

  // Load history and connect WebSocket (only when uuid changes)
  useEffect(() => {
    if (!uuid) return;
    
    fetch(`${HISTORY_ENDPOINT}?uuid=${uuid}`)
      .then(res => res.json())
      .then(data => setMessages(data.messages || []));
    
    const ws = new WebSocket(`${CHAT_ENDPOINT}?uuid=${uuid}`);
    wsRef.current = ws;
    
    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        console.log('WebSocket message received:', msg);
        
        // If msg.type is missing, treat it as a regular chat message
        if (!msg.type || msg.type === "message") {
          console.log('Adding message and stopping typing');
          const newMessage = msg.data || msg;
          setMessages(prev => [...prev, newMessage]);
          setIsAgentTyping(false);
          console.log('isAgentTyping set to false');
          
          // Play gentle notification sound for agent messages
          if (newMessage.sender && newMessage.sender !== 'user') {
            playUserNotificationSound();
          }
        } else if (msg.type === "history") {
          console.log('Loading message history');
          setMessages(msg.messages || []);
          setIsAgentTyping(false); // Clear typing when loading history
        } else if (msg.type === "typing") {
          console.log('Agent started typing');
          setLastTypingTime(Date.now());
          setRenderKey(prev => prev + 1);
          console.log('lastTypingTime updated');
        } else if (msg.type === "stop_typing") {
          console.log('Agent stopped typing');
          setLastTypingTime(0); // Clear typing
          setRenderKey(prev => prev + 1);
        }
      } catch (err) {
        console.error('Failed to parse WebSocket message:', err);
        // Clear typing state on any error
        setIsAgentTyping(false);
      }
    };
    
    ws.onclose = () => {
      setIsAgentTyping(false);
      setIsUserTyping(false);
    };
    
    return () => ws.close();
  }, [uuid]); // Only reconnect when uuid changes, not when open changes

  // Auto-scroll to bottom on new messages or typing indicator
  useEffect(() => {
    if (scrollRef.current)
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, isAgentTyping]);

  // Reset typing state when widget closes
  useEffect(() => {
    if (!open) {
      setIsAgentTyping(false);
      setIsUserTyping(false);
    }
  }, [open]);

  // Send user message
  const sendMessage = () => {
    if (!input.trim() || !wsRef.current) return;
    const msg = { sender: "user", text: input.trim(), time: Date.now() };
    wsRef.current.send(JSON.stringify(msg));
    setInput("");
    setIsUserTyping(false);
    wsRef.current.send(JSON.stringify({ type: "stop_typing" }));
  };

  // Notify typing (debounced)
  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value);
    if (!wsRef.current) return;

    if (!isUserTyping) {
      wsRef.current.send(JSON.stringify({ type: "typing" }));
      setIsUserTyping(true);
    }

    if (typingTimeout.current) clearTimeout(typingTimeout.current);

    typingTimeout.current = setTimeout(() => {
      wsRef.current?.send(JSON.stringify({ type: "stop_typing" }));
      setIsUserTyping(false);
    }, 1500); // stops typing if idle for 1.5s
  };

  // Mobile-friendly close on Esc
  useEffect(() => {
    if (!open) return;
    const close = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [open]);

  // Optional: Greet user on open (only once)
  useEffect(() => {
    if (open && messages.length === 0) {
      setTimeout(() => {
        setMessages([{
          sender: "agent",
          text: "Hi, I'm Bizzy, the concierge at Hivetivity. Ask me a question and I will summon a human for you now! Feel free to keep working - I wont't forget what you send me🐝",
          time: Date.now(),
        }]);
      }, 450);
    }
    // eslint-disable-next-line
  }, [open]);

  return (
    <>
      {/* Floating Bubble */}
      {!open && (
        <button
          className="chat-bubble-btn"
          onClick={() => setOpen(true)}
          aria-label="Open chat"
        >
          <Avatar />
          {showAttentionDots && (
            <div className="attention-dots">
              <span className="attention-dot"></span>
              <span className="attention-dot"></span>
              <span className="attention-dot"></span>
            </div>
          )}
        </button>
      )}
      {/* Panel */}
      {open && (
        <div className="chat-widget-panel">
          <div className="chat-header">
            <Avatar />
            <span style={{ fontWeight: 600, marginLeft: 12, fontSize: 17 }}>
              Chat with Hivetivity
            </span>
            <button className="chat-close-btn" onClick={() => setOpen(false)} aria-label="Close chat">&times;</button>
          </div>
          <div className="chat-messages" ref={scrollRef}>
            {messages.map((m, i) => (
              <div
                key={i}
                className={`chat-msg ${m.sender === "user" ? "user" : "agent"}`}
              >
                {m.sender === "agent" && <BizzyAvatar />}
                <span className="chat-bubble">{m.text}</span>
                <span className="chat-time">{new Date(m.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
              </div>
            ))}
            {isTypingActive && (
              <div className="chat-msg agent" key={`typing-${renderKey}`}>
                <BizzyAvatar />
                <span className="chat-bubble typing">
                  <span className="dot"></span>
                  <span className="dot"></span>
                  <span className="dot"></span>
                  <span style={{ marginLeft: 7, color: "#bbb" }}>Bizzy is typing…</span>
                </span>
              </div>
            )}
          </div>
          <form
            className="chat-input-bar"
            onSubmit={e => {
              e.preventDefault();
              sendMessage();
            }}
          >
            <input
              value={input}
              onChange={handleInput}
              placeholder="Type a message…"
              autoFocus
              onFocus={() => wsRef.current?.send(JSON.stringify({ type: "typing" }))}
              onBlur={() => wsRef.current?.send(JSON.stringify({ type: "stop_typing" }))}
            />
            <button type="submit" disabled={!input.trim()}>Send</button>
          </form>
        </div>
      )}
      <style>{`
        .chat-bubble-btn {
          position: fixed;
          right: 30px;
          bottom: 30px;
          z-index: 1000;
          background: none;
          border: none;
          cursor: pointer;
          box-shadow: 0 6px 24px rgba(50,80,120,0.25);
          border-radius: 50%;
          width: 80px;
          height: 80px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          transform: scale(1);
          animation: subtle-pulse 4s ease-in-out infinite;
        }
        .chat-bubble-btn:hover { 
          box-shadow: 0 12px 40px rgba(50,80,120,0.35);
          transform: scale(1.1);
          animation: none;
        }
        .chat-bubble-btn:active {
          transform: scale(1.05);
        }
        @keyframes subtle-pulse {
          0%, 100% { 
            box-shadow: 0 6px 24px rgba(50,80,120,0.25);
          }
          50% { 
            box-shadow: 0 8px 30px rgba(50,80,120,0.3);
          }
        }
        .attention-dots {
          position: absolute;
          top: -8px;
          left: -8px;
          display: flex;
          gap: 3px;
          background: linear-gradient(135deg, #4ba3c7 0%, #5bb5d8 100%);
          padding: 8px 12px;
          border-radius: 20px;
          box-shadow: 0 4px 16px rgba(75,163,199,0.3), 0 2px 8px rgba(75,163,199,0.2);
          border: 2px solid white;
          animation: attention-bubble-entrance 0.3s ease-out;
        }
        .attention-dot {
          display: inline-block;
          width: 7px;
          height: 7px;
          background: white;
          border-radius: 50%;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          animation: attention-bounce 1.4s infinite ease-in-out;
        }
        .attention-dot:nth-child(1) { animation-delay: 0s; }
        .attention-dot:nth-child(2) { animation-delay: 0.2s; }
        .attention-dot:nth-child(3) { animation-delay: 0.4s; }
        @keyframes attention-bounce {
          0%, 80%, 100% {
            opacity: 0.6;
            transform: translateY(0) scale(1);
          }
          40% {
            opacity: 1;
            transform: translateY(-5px) scale(1.15);
          }
        }
        @keyframes attention-bubble-entrance {
          0% {
            opacity: 0;
            transform: scale(0.8) translateY(10px);
          }
          100% {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
        .chat-widget-panel {
          position: fixed;
          right: 30px;
          bottom: 65px;
          width: 400px;
          max-width: 98vw;
          height: 550px;
          max-height: 90vh;
          background: #fff;
          border-radius: 22px;
          box-shadow: 0 8px 48px 0 rgba(50,80,120,0.20);
          display: flex;
          flex-direction: column;
          z-index: 1100;
          animation: chatFadeIn 0.25s;
        }
        @keyframes chatFadeIn {
          0% { transform: translateY(40px) scale(0.94); opacity: 0; }
          100% { transform: none; opacity: 1; }
        }
        .chat-header {
          display: flex;
          align-items: center;
          padding: 14px 20px 11px 18px;
          background: #f5f8fa;
          border-bottom: 1px solid #ececec;
          border-radius: 22px 22px 0 0;
          font-size: 18px;
        }
        .chat-avatar { flex-shrink: 0; }
        .chat-close-btn {
          margin-left: auto;
          font-size: 28px;
          background: none;
          border: none;
          cursor: pointer;
          color: #999;
          line-height: 1;
          padding: 0 10px;
        }
        .chat-messages {
          flex: 1 1 auto;
          overflow-y: auto;
          padding: 23px 16px 50px 16px;
          background: #fafcff;
          display: flex;
          flex-direction: column;
        }
        .chat-msg { display: flex; align-items: flex-end; margin: 0 0 14px 0;}
        .chat-msg.user { justify-content: flex-end; }
        .chat-msg.agent { justify-content: flex-start;}
        .chat-bubble {
          display: inline-block;
          padding: 12px 20px;
          background: #f3fafe;
          color: #242f3a;
          border-radius: 22px;
          font-size: 16px;
          max-width: 74%;
          word-break: break-word;
          margin: 0 10px;
          box-shadow: 0 1px 4px 0 rgba(75,163,199,0.08);
        }
        .chat-msg.user .chat-bubble {
          background: ${BRAND};
          color: #fff;
          border-bottom-right-radius: 12px;
          border-bottom-left-radius: 22px;
        }
        .chat-msg.agent .chat-bubble {
          background: #f3fafe;
          color: #242f3a;
          border-bottom-left-radius: 12px;
          border-bottom-right-radius: 22px;
        }
        .chat-bubble.typing {
          font-style: italic;
          font-weight: 400;
          background: #e2f6ff;
          color: #4ba3c7;
          min-width: 62px;
        }
        .dot {
          display: inline-block;
          width: 10px;
          height: 10px;
          margin: 0 3px;
          background: #4ba3c7;
          border-radius: 50%;
          opacity: 0.2;
          animation: bounce 1.4s infinite ease-in-out;
          will-change: transform, opacity;
        }
        .dot:nth-child(1) { animation-delay: 0s; }
        .dot:nth-child(2) { animation-delay: 0.2s; }
        .dot:nth-child(3) { animation-delay: 0.4s; }
        @keyframes bounce {
          0%, 80%, 100% {
            opacity: 0.2;
            transform: translateY(0);
          }
          40% {
            opacity: 1;
            transform: translateY(-8px);
          }
        }
        .chat-time {
          font-size: 13px;
          color: #bbb;
          margin-left: 5px;
          margin-right: 3px;
          align-self: flex-end;
        }
        .chat-input-bar {
          display: flex;
          align-items: center;
          padding: 14px 18px;
          background: #f5f8fa;
          border-top: 1px solid #ececec;
          border-radius: 0 0 22px 22px;
        }
        .chat-input-bar input {
          flex: 1;
          padding: 11px 15px;
          border-radius: 10px;
          border: 1.5px solid #d7e5ee;
          font-size: 16px;
          outline: none;
          margin-right: 13px;
        }
        .chat-input-bar button {
          padding: 10px 23px;
          border-radius: 10px;
          border: none;
          background: ${BRAND};
          color: #fff;
          font-weight: 600;
          font-size: 16px;
          cursor: pointer;
        }
        @media (max-width: 600px) {
          .chat-widget-panel {
            width: 98vw !important;
            height: 96vh !important;
            right: 1vw;
            bottom: 2vw;
            border-radius: 14px;
          }
        }
      `}</style>
    </>
  );
});

export const ChatWidget: React.FC = () => {
  const location = useLocation();
  
  // Hide chat widget in parent portal to avoid UI crowding
  if (location.pathname.startsWith('/parent-portal')) {
    return null;
  }
  
  return <ChatWidgetInner />;
};
